import React from 'react'
import { useNavigate } from 'react-router-dom'
import LecturesSubNav from '../components/LecturesSubNav'
import SharedFilters from '../../../components/SharedFilters'
import AppPagination from '../../../components/AppPagination'
import AppActionMenu from '../../../components/AppActionMenu'
import useTimetableListScreen from './hooks/useTimetableListScreen'

export default function TimetableListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useTimetableListScreen()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Top Canopy: Header & SubNav ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Academic &amp; Timetable</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Time Table Schedule
          </h1>
        </div>
      </section>

      {/* ── Shared Filter Controls ── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        courseFilter={state.courseFilter}
        facultyFilter={state.facultyFilter}
        batchFilter={state.batchFilter}
        subjectFilter={state.subjectFilter}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setCourseFilter={actions.setCourseFilter}
        setFacultyFilter={actions.setFacultyFilter}
        setBatchFilter={actions.setBatchFilter}
        setSubjectFilter={actions.setSubjectFilter}
        onReset={actions.resetFilters}
        onSearch={() => { }}

        dayFilter={state.dayFilter}
        setDayFilter={actions.setDayFilter}
        dayOptions={state.options.days}

        searchFieldOptions={state.options.searchFields}
        companyOptions={state.options.companies}
        branchOptions={state.options.branches}
        courseOptions={state.options.courses}
        facultyOptions={state.options.faculties}
        batchOptions={state.options.batches}
        subjectOptions={state.options.subjects}

        visibleAccordionFilters={['company', 'branch', 'course', 'faculty', 'day', 'batch', 'subject']}

        tools={[
          { key: 'export-excel', label: 'Export Excel', icon: 'table_view' },
          { key: 'export-pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
        ]}
        onToolSelect={(toolKey) => {
          if (toolKey === 'export-excel') {
            actions.handleExportExcel()
          } else if (toolKey === 'export-pdf') {
            actions.handleExportPdf()
          }
        }}
      />

      {/* ── Background Export Progress Banner ── */}
      {state.isExporting && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-3.5 text-xs text-primary animate-in fade-in">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
            <span className="font-bold">Generating background export...</span>
          </div>
        </div>
      )}

      {/* ── Day Filter Quick Bar ── */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant mr-1">Day:</span>
        <button
          type="button"
          onClick={() => actions.setDayFilter('All')}
          className={`rounded-xl px-3.5 py-1.5 text-xs font-bold transition shadow-2xs ${state.dayFilter === 'All'
            ? 'bg-primary text-on-primary shadow-sm'
            : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
        >
          All Days
        </button>
        {state.options.days.map((d) => (
          <button
            key={d.value}
            type="button"
            onClick={() => actions.setDayFilter(d.value)}
            className={`rounded-xl px-3 py-1.5 text-xs font-bold transition shadow-2xs ${state.dayFilter === d.value
              ? 'bg-primary text-on-primary shadow-sm'
              : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
              }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* ── Timetable GridView Table ── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-xs text-on-surface">
            <thead className="border-b border-outline-variant/25 bg-surface-container-low/70 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              <tr>
                <th className="px-5 py-3.5">Faculty</th>
                <th className="px-5 py-3.5">Day / Time</th>
                <th className="px-5 py-3.5">Course &amp; Subject</th>
                <th className="px-5 py-3.5">Branch / Batch</th>
                <th className="px-4 py-3.5 text-center">Classroom</th>
                <th className="px-4 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {state.records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-14 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-4xl text-outline-variant">
                        event_busy
                      </span>
                      <p className="text-sm font-bold text-on-surface">No timetable entries found</p>
                      <p className="text-xs text-on-surface-variant">
                        Try adjusting your filters or click "Add New Entry" to schedule classes.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                state.records.map((item) => (
                  <tr key={item.id} className="group hover:bg-surface-container-low/40 transition-colors">

                    {/* Faculty */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold text-xs">
                          {item.faculty?.charAt(0) || 'F'}
                        </div>
                        <div>
                          <p className="font-bold text-on-surface text-xs">{item.faculty}</p>
                          <p className="text-[11px] text-on-surface-variant">{item.company}</p>
                        </div>
                      </div>
                    </td>

                    {/* Day / Time */}
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex w-fit items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                          <span className="material-symbols-outlined text-xs">calendar_today</span>
                          {item.dayOfWeek}
                        </span>
                        <span className="text-xs font-semibold text-on-surface">
                          {typeof item.planTime === 'object' && item.planTime?.hour
                            ? `${item.planTime.hour}:${item.planTime.minute} ${item.planTime.period} – ${item.tillTime.hour}:${item.tillTime.minute} ${item.tillTime.period}`
                            : `${item.planTime || ''} – ${item.tillTime || ''}`}
                        </span>
                      </div>
                    </td>

                    {/* Course & Subject */}
                    <td className="px-5 py-3.5">
                      <p className="font-bold text-on-surface text-xs">{item.course}</p>
                      <p className="text-[11px] font-semibold text-on-surface-variant">{item.subject}</p>
                    </td>

                    {/* Branch & Batch */}
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-outline">Branch:</span>
                          <span className="font-semibold text-on-surface">{item.branch}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-on-surface-variant">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-outline">Batch:</span>
                          <span>{item.batch}</span>
                        </div>
                      </div>
                    </td>

                    {/* Classroom */}
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center gap-1 rounded-xl bg-surface-container-high px-2.5 py-1 text-xs font-bold text-on-surface">
                        <span className="material-symbols-outlined text-xs text-primary">meeting_room</span>
                        {item.classroom}
                      </span>
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex justify-end">
                        <AppActionMenu
                          triggerLabel=""
                          triggerIcon="more_vert"
                          showChevron={false}
                          triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                          align="right"
                          items={[
                            { key: 'edit', label: 'Edit', icon: 'edit' },
                            { key: 'delete', label: 'Delete', icon: 'delete', danger: true },
                          ]}
                          onSelect={(actionKey) => {
                            if (actionKey === 'edit') {
                              navigate(`/lectures/timetable/entry/${item.id}`)
                            } else if (actionKey === 'delete') {
                              actions.handleDelete(item.id, item.faculty, item.dayOfWeek)
                            }
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Pagination */}
        <AppPagination
          currentPage={state.currentPage}
          totalPages={state.totalPages}
          pageSize={state.pageSize}
          totalEntries={state.total}
          onPageChange={actions.setCurrentPage}
          onPageSizeChange={actions.setPageSize}
          pageSizeOptions={[10, 25, 50, 75, 100]}
        />
      </section>
    </div>
  )
}
